// SiteLifecycle — the site's lifecycle DAYS and their correction. The row holds
// four declared calendar days (activated · the LAST pause's start and end ·
// decommissioned — the pause stays once resumed, so "Last pause" reads from –
// to); each shows only when the row holds it, beside Created (the clock).
// `LifecycleDayFields` is the shared read fragment — the page's Lifecycle card
// and the peek render the same set (one truth for "what the dates say").
// `CorrectDatesDrawer` is the correction — the Lifecycle card's act ("Correct
// dates", a row of the site's registry — `SiteActions.tsx`; the peek's Edit
// menu holds the same row): a drawer (ADR-038) over the held days; a held day
// can move, never be cleared; the form mirrors the ordering law on its live
// values (a wrong order never leaves the browser) and the server owns it
// (INVALID_LIFECYCLE_DATE surfaces on the form's refusal line — the shell maps
// the code, so the error is rethrown as is). The correction rides the site's
// row stamp (ADR-084): the shell seeds from the site as it is at each open,
// sends the stamp and only the days moved since, and a stale save shows the
// line with Load the latest. "Today" is the site's day (ADR-082 §6), never the
// browser's.

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Field } from '../../components/patterns/DescriptionList.js';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { useToast } from '../../components/ui/Toast.js';
import { fetchSiteDetail, sitesKeys, useCorrectLifecycleDates } from '../../hooks/useSites.js';
import { formatDate } from '../../lib/format.js';
import { todayIn } from '../../lib/period.js';
import { useWeekStart } from '../../hooks/useWeekStart.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { LIFECYCLE_DAY_FIELDS, type LifecycleDatesCorrectionRequest, type LifecycleDayField } from '@ems-app/schemas/sites/requests';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';

type T = ReturnType<typeof useTranslation>['t'];
type DayValues = Record<LifecycleDayField, string | null>;
type LifecycleFacts = Pick<SiteDetail, 'status' | 'activated_on' | 'suspended_on' | 'resumed_on' | 'decommissioned_on' | 'suspend_reason'>;

const FIELD_LABEL: Record<LifecycleDayField, string> = {
  activated_on: 'detail.fields.activated',
  suspended_on: 'detail.fields.suspended',
  resumed_on: 'detail.fields.resumed',
  decommissioned_on: 'detail.fields.decommissioned',
};

/** The read fragment: Activated on · Suspended since (open pause) or Last pause
 *  (from – to) · the pause's reason · Decommissioned on — each only when held. */
export function LifecycleDayFields({ site, t }: { site: LifecycleFacts; t: T }) {
  const day = (v: string) => formatDate(v, 'date') ?? v;
  return (
    <>
      {site.activated_on && <Field label={t('detail.fields.activated')}>{day(site.activated_on)}</Field>}
      {site.suspended_on && site.resumed_on === null && <Field label={t('detail.fields.suspended')}>{day(site.suspended_on)}</Field>}
      {site.suspended_on && site.resumed_on && (
        <Field label={t('detail.fields.lastPause')}>
          <span dir="auto">
            {day(site.suspended_on)} – {day(site.resumed_on)}
          </span>
        </Field>
      )}
      {site.suspended_on && site.suspend_reason && <Field label={t('detail.fields.suspendReason')}>{site.suspend_reason}</Field>}
      {site.decommissioned_on && <Field label={t('detail.fields.decommissioned')}>{day(site.decommissioned_on)}</Field>}
    </>
  );
}

export interface CorrectDatesDrawerProps {
  projectId: string;
  site: SiteDetail;
  open: boolean;
  onClose: () => void;
}

/** The correction drawer — the days the row HOLDS, each a picker floored by its saved neighbour and capped at
 *  the site's today; mounted by its host only while open. */
export function CorrectDatesDrawer({ projectId, site, open, onClose }: CorrectDatesDrawerProps) {
  const { t } = useTranslation('sites');
  const { t: tCommon } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const correct = useCorrectLifecycleDates(projectId);
  // The site's own screen reads in the site's zone (ADR-082 §2); a draft without one in the project's.
  const display = useDisplayZone({ kind: 'site', projectId, zone: site.timezone_iana });
  // The ceiling of "any day up to today" is the site's day (ADR-082 §6), never the browser's.
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  const today = todayIn(zone);
  const weekStart = useWeekStart(projectId);

  // The days the row HOLDS — the only ones a correction may move (a draft holds none).
  const held = useMemo(() => LIFECYCLE_DAY_FIELDS.filter((f) => site[f] !== null), [site]);
  const schema = useMemo(
    () =>
      z
        .object({
          activated_on: z.string().nullable(),
          suspended_on: z.string().nullable(),
          resumed_on: z.string().nullable(),
          decommissioned_on: z.string().nullable(),
        })
        .superRefine((v, ctx) => {
          for (const f of held) {
            if (v[f] === null) ctx.addIssue({ code: 'custom', path: [f], message: t('lifecycle.dateRequired') });
            else if (v[f]! > today) ctx.addIssue({ code: 'custom', path: [f], message: t('lifecycle.correct.dateFuture') });
          }
          // The ordering law on the LIVE values (the server's floors, mirrored):
          // the later day is blamed — activated ≤ suspended ≤ resumed; decommissioned after all.
          const order: Array<[LifecycleDayField, LifecycleDayField]> = [
            ['suspended_on', 'activated_on'],
            ['resumed_on', 'suspended_on'],
            ['decommissioned_on', 'activated_on'],
            ['decommissioned_on', 'suspended_on'],
            ['decommissioned_on', 'resumed_on'],
          ];
          for (const [later, floor] of order) {
            const l = v[later];
            const fl = v[floor];
            if (l !== null && fl !== null && l < fl) ctx.addIssue({ code: 'custom', path: [later], message: t('lifecycle.correct.dateOrder', { other: t(FIELD_LABEL[floor]) }) });
          }
        }) as unknown as z.ZodType<DayValues, DayValues>,
    [held, t, today],
  );

  // Soft floors for the pickers — the saved neighbours; the server's law is the truth.
  const floorOf = (f: LifecycleDayField): string | undefined => {
    switch (f) {
      case 'activated_on':
        return undefined;
      case 'suspended_on':
        return site.activated_on ?? undefined;
      case 'resumed_on':
        return site.suspended_on ?? undefined;
      case 'decommissioned_on':
        return site.resumed_on ?? site.suspended_on ?? site.activated_on ?? undefined;
    }
  };

  const shell = useFormShell<DayValues, SiteDetail>({
    schema,
    row: site,
    values: (r) => ({ activated_on: r?.activated_on ?? null, suspended_on: r?.suspended_on ?? null, resumed_on: r?.resumed_on ?? null, decommissioned_on: r?.decommissioned_on ?? null }),
    // The fresh site: a quiet read (a site gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(qc, sitesKeys.detail(projectId, site.id), () => fetchSiteDetail(projectId, site.id, true)),
    onSubmit: async (next, { stamp, changed }) => {
      // Only the days moved since the drawer opened — never a diff against the live row (ADR-084 §6).
      const body: LifecycleDatesCorrectionRequest = { row_version: stamp };
      for (const f of held) if (f in changed && next[f] !== null) body[f] = next[f]!;
      if (!LIFECYCLE_DAY_FIELDS.some((f) => body[f] !== undefined)) return; // nothing moved
      // A server refusal (INVALID_LIFECYCLE_DATE) propagates as the Axios error —
      // useFormShell maps its code to the errors namespace for the refusal line.
      await correct.mutateAsync({ siteId: site.id, body });
      toast.success(t('lifecycle.correct.done'));
    },
    onSuccess: onClose,
  });

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('lifecycle.correct.edit')} description={site.name} submitLabel={tCommon('actions.save')}>
      <p className="text-xs text-[var(--color-text-secondary)]">{t('lifecycle.correct.help')}</p>
      {held.map((f) => (
        <Controller
          key={f}
          control={shell.form.control}
          name={f}
          render={({ field, fieldState }) => (
            <DatePicker label={t(FIELD_LABEL[f])} value={field.value} onChange={field.onChange} minValue={floorOf(f)} maxValue={today} weekStart={weekStart} zone={zone} error={fieldState.error?.message} />
          )}
        />
      ))}
    </DrawerForm>
  );
}
